import Link from "next/link";
import type { ReactElement } from "react";

type BaseCommonButtonProps = {
  text: string;
  version?: string;
  isSecondary?: boolean;
  isSecondaryBg?: boolean;
  isRounded?: boolean;
  hasIcon?: boolean;
  watchIcon?: boolean;
  customIcon?: ReactElement;
  target?: string;
  textWeightMedium?: boolean;
  textSizeBtnClass?: string;
};

type CommonButtonWithUrl = BaseCommonButtonProps & {
  url: string;
  onClick?: never;
};

type CommonButtonWithOnClick = BaseCommonButtonProps & {
  url?: string;
  onClick: () => void;
};

type CommonButtonProps = CommonButtonWithUrl | CommonButtonWithOnClick;

const CommonButton = ({
  text = "Button",
  url = "/",
  version = "gray",
  isSecondary = false,
  isSecondaryBg,
  isRounded,
  hasIcon,
  watchIcon,
  customIcon,
  target,
  textWeightMedium,
  textSizeBtnClass = "text-[14px]",
  onClick,
}: CommonButtonProps) => {
  if (isSecondary) {
    return (
      <Link
        className={`flex w-fit items-center border-[0.8px] border-solid ${isSecondary ? "border-outlineBorder hover:border-white" : "border-outlineBorder"} py-[10px] duration-300 ease-in-out ${isRounded ? "rounded-[50px]" : "rounded-[4px]"
          } ${textSizeBtnClass} ${hasIcon || watchIcon || customIcon ? "flex items-center gap-2 px-4 pr-3" : "lg:px-8"} ${isSecondary ? "!bg-transparent px-4 text-outlineText hover:text-white xl:px-6" : ""} ${textWeightMedium ? "font-medium" : "font-semibold"} `}
        href={url}
        target={target}
        onClick={onClick}
      >
        <span>{text}</span>
        {customIcon && <span> {customIcon} </span>}
        {watchIcon ? (
          <span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
            >
              <path
                d="M6 12.4997V9.82968C6 6.51968 8.35 5.15968 11.22 6.81968L13.53 8.15968L15.84 9.49968C18.71 11.1597 18.71 13.8697 15.84 15.5297L13.53 16.8697L11.22 18.2097C8.35 19.8397 6 18.4897 6 15.1697V12.4997Z"
                stroke="#F8F8FF"
                strokeWidth="1.5"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        ) : (
          ""
        )}

        {hasIcon ? (
          <span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M8 21L16 12L8 3"
                stroke="#F8F8FF"
                strokeWidth="1.331"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        ) : (
          ""
        )}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button
        className={`flex w-fit items-center py-[10px] duration-300 ease-in-out ${version == "gray"
            ? "bg-btnGray px-4 text-white xl:px-6"
            : "bg-btnWhite px-4 font-medium text-textBlack xl:px-6"
          } ${version == "white" ? "bg-white text-outlineText" : ""} border-[0.8px] border-solid border-outlineBorder ${version == "black" ? "bg-[#000000] text-white" : ""} ${isRounded ? "rounded-[50px]" : "rounded-[4px]"
          } ${textSizeBtnClass} ${hasIcon || watchIcon || customIcon ? "flex items-center gap-2 px-4 pr-3" : "lg:px-8"} ${isSecondaryBg ? "border-none !bg-brand px-4 text-white hover:!bg-brand-hover xl:px-6" : ""} ${textWeightMedium ? "font-medium" : "font-semibold"} `}
        onClick={onClick}
      >
        <span>{text}</span>
      </button>
    );
  }
  return (
    <Link
      className={`flex w-fit items-center py-[10px] duration-300 ease-in-out ${version == "gray"
          ? "bg-btnGray px-4 text-white xl:px-6"
          : "bg-btnWhite px-4 font-medium text-textBlack xl:px-6"
        } ${version == "white" ? "bg-white text-outlineText" : ""} ${version == "black" ? "bg-[#000000] text-white" : ""} ${version == "transparent" ? "border-white bg-transparent text-white" : ""} border-[0.8px] border-solid border-outlineBorder ${isRounded ? "rounded-[50px]" : "rounded-[4px]"
        } ${textSizeBtnClass} ${hasIcon || watchIcon || customIcon ? "flex items-center gap-2 px-4 pr-3" : "lg:px-8"} ${isSecondaryBg ? "border-none !bg-brand px-4 text-white hover:!bg-brand-hover xl:px-6" : ""} ${textWeightMedium ? "font-medium" : "font-semibold"} `}
      href={url}
      target={target === "none" ? undefined : "_blank"}
    >
      <span>{text}</span>
      {customIcon && <span> {customIcon} </span>}
      {watchIcon ? (
        <span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="25"
            viewBox="0 0 24 25"
            fill="none"
          >
            <path
              d="M6 12.4997V9.82968C6 6.51968 8.35 5.15968 11.22 6.81968L13.53 8.15968L15.84 9.49968C18.71 11.1597 18.71 13.8697 15.84 15.5297L13.53 16.8697L11.22 18.2097C8.35 19.8397 6 18.4897 6 15.1697V12.4997Z"
              stroke="#202020"
              strokeWidth="1.5"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      ) : (
        ""
      )}
      {hasIcon ? (
        <span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M8 21L16 12L8 3"
              stroke="#F8F8FF"
              strokeWidth="1.331"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      ) : (
        ""
      )}
    </Link>
  );
};

export default CommonButton;
